import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import type { AiEmailUpdateDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { Table } from "@/components/Table/Table";
import { useSlug } from "@/hooks/useSlug";
import { aiEmailUpdateMutations } from "@/queries/ai-email-updates/mutations";
import { useAiEmailUpdates } from "@/queries/ai-email-updates/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function AiEmailUpdateUnsubscribe(): React.ReactNode {
  const queries = useAiEmailUpdates();
  const { data = [] } = useQuery(queries.getUpdates());
  const { mutate: unsubscribeAiEmailUpdate, isPending: isUnsubscribing } =
    aiEmailUpdateMutations.useUnsubscribeAiEmailUpdate();
  const { t } = useTranslation();
  const slug = useSlug();
  const navigate = useNavigate();

  const param = useParams<{ id?: string }>();

  const foundItem = param.id ? data.find((item) => item.id === param.id) : undefined;

  const columns = useMemo(() => {
    const helper = createColumnHelper<AiEmailUpdateDto>();

    return [
      helper.accessor("prompt", {
        header: t("page.ai-email-update.prompt"),
      }),
      helper.accessor("timePeriod", {
        header: t("page.ai-email-update.time-period"),
        cell: (cell) => {
          const timePeriod = cell.getValue();

          return (
            <span>
              {timePeriod === "weekly"
                ? t("page.ai-email-update.time-period-weekly")
                : t("page.ai-email-update.time-period-monthly")}
            </span>
          );
        },
        maxSize: 80,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.unsubscribe"),
              },
              value: "unsubscribe",
              "data-testid": "context-menu-unsubscribe-btn",
              icon: <Trash02Icon />,
              type: "button",
              callbackFn: () => unsubscribeAiEmailUpdate(id),
            },
          ];

          return (
            <div className="flex justify-end px-2">
              <ContextMenu items={menuActions} />
            </div>
          );
        },
        maxSize: 50,
        minSize: 50,
        size: 50,
      }),
    ];
  }, [t, unsubscribeAiEmailUpdate]);

  const tableInstance = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DocumentPaper title={t("page.ai-email-update.title")}>
      <PageGrid.Grid>
        <PageGrid.Item size="100%">
          <div className="flex w-full flex-col gap-2">
            <Table
              table={tableInstance}
              data-testid="ai-email-update-list"
              getRowProps={() => ({
                "data-testid": "ai-email-update-item",
              })}
            />
          </div>
        </PageGrid.Item>
      </PageGrid.Grid>
      <ConfirmModal
        title={t("page.ai-email-update.unsubscribe.title")}
        description={t("page.ai-email-update.unsubscribe.description", {
          prompt: foundItem?.prompt || "-",
        })}
        isLoading={isUnsubscribing}
        theme="danger"
        onReject={() => void navigate(routes.emailUnsubscribe.aiUpdateList({ slug }))}
        rejectBtnProps={{
          "data-testid": "cancel-unsubscribe-btn",
        }}
        onOpenChange={(open) => {
          if (!open) {
            void navigate(routes.emailUnsubscribe.aiUpdateList({ slug }));
          }
        }}
        onResolve={() => {
          if (foundItem) {
            unsubscribeAiEmailUpdate(foundItem.id);
          }
        }}
        resolveBtnProps={{
          text: t("common.action.unsubscribe"),
          "data-testid": "confirm-unsubscribe-btn",
        }}
        isOpened={foundItem !== undefined}
        shouldCloseOnEsc
      />
    </DocumentPaper>
  );
}
